import { useEffect, useState, type FormEvent } from 'react';
import { Button, DotLoading, Toast } from 'antd-mobile';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { supabase, updatePassword } from '@ubus/shared';

function ResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [ready, setReady] = useState(false);
  const [canReset, setCanReset] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      if (event === 'PASSWORD_RECOVERY' || (session && event === 'SIGNED_IN')) {
        setCanReset(true);
      }
      setReady(true);
    });

    void supabase.auth.getSession().then(({ data: { session } }) => {
      if (cancelled) return;
      if (session) setCanReset(true);
      setReady(true);
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      Toast.show({ icon: 'fail', content: t('auth.passwordMismatch') });
      return;
    }
    if (password.length < 8) {
      Toast.show({ icon: 'fail', content: t('auth.passwordMinLength') });
      return;
    }
    setLoading(true);
    try {
      const { error } = await updatePassword(password);
      if (error) {
        Toast.show({ icon: 'fail', content: error.message });
        return;
      }
      Toast.show({ icon: 'success', content: t('auth.resetSuccess') });
      await supabase.auth.signOut();
      navigate('/login', { replace: true });
    } finally {
      setLoading(false);
    }
  };

  if (!ready) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <DotLoading color="primary" />
      </div>
    );
  }

  return (
    <div className="driver-login">
      <div className="driver-login-main">
        <div className="driver-login-brand">
          <span className="driver-login-brand-text">ubus</span>
        </div>
        <h1 className="driver-login-heading">{t('auth.resetTitle')}</h1>
        <p className="driver-login-subheading">{t('auth.resetPortalLabel')}</p>

        {!canReset ? (
          <p className="driver-login-forgot-hint" style={{ marginTop: 24 }}>
            {t('auth.resetInvalidLink')}
          </p>
        ) : (
          <form onSubmit={(e) => void handleSubmit(e)}>
            <label className="driver-login-label" htmlFor="reset-password">
              {t('auth.resetNewPassword')}
            </label>
            <input
              id="reset-password"
              className="driver-login-field"
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <label className="driver-login-label" htmlFor="reset-confirm" style={{ marginTop: 12 }}>
              {t('auth.resetConfirmPassword')}
            </label>
            <input
              id="reset-confirm"
              className="driver-login-field"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
            <Button
              block
              color="primary"
              size="large"
              loading={loading}
              type="submit"
              style={{ marginTop: 20 }}
            >
              {t('auth.resetSavePassword')}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}

export default ResetPassword;
